// Minimal Server-Sent Events reader over fetch — unlike EventSource it can send the region token
// as an Authorization header instead of leaking it into a URL (and so into proxy logs).
import { ApiError, humanise } from "./http";
import { ApiErrorBody } from "./schemas";

export interface SseMessage {
  event: string;
  data: string;
  id?: string;
}

/** Incremental SSE parser: feed it text chunks, it returns every message completed so far. */
export function createSseParser(): (chunk: string) => SseMessage[] {
  let buffer = "";
  return (chunk) => {
    buffer += chunk.replace(/\r\n?/g, "\n");
    const out: SseMessage[] = [];
    let end = buffer.indexOf("\n\n");
    while (end !== -1) {
      const block = buffer.slice(0, end);
      buffer = buffer.slice(end + 2);
      let event = "message";
      let id: string | undefined;
      const data: string[] = [];
      for (const line of block.split("\n")) {
        if (!line || line.startsWith(":")) continue;
        const colon = line.indexOf(":");
        const field = colon === -1 ? line : line.slice(0, colon);
        const value = colon === -1 ? "" : line.slice(colon + 1).replace(/^ /, "");
        if (field === "event") event = value;
        else if (field === "data") data.push(value);
        else if (field === "id") id = value;
      }
      if (data.length) out.push({ event, data: data.join("\n"), ...(id !== undefined ? { id } : {}) });
      end = buffer.indexOf("\n\n");
    }
    return out;
  };
}

/**
 * POSTs `body` and streams the SSE answer (Ask). A non-2xx answer is an ordinary JSON error envelope,
 * thrown as `ApiError` so callers can tell "not configured" (503) from a failure.
 */
export async function postSse(
  url: string,
  { token, body, signal, onMessage }: { token: string; body: unknown; signal: AbortSignal; onMessage: (m: SseMessage) => void },
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: { accept: "text/event-stream", "content-type": "application/json", authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
      credentials: "omit",
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError(0, "network", "Can't reach your project's region. Check your connection and try again.");
  }
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => "");
    let json: unknown;
    try {
      json = text ? JSON.parse(text) : undefined;
    } catch {
      json = undefined;
    }
    const parsed = ApiErrorBody.safeParse(json);
    if (parsed.success) throw new ApiError(res.status, parsed.data.error.code, humanise(parsed.data.error.message));
    throw new ApiError(res.status, `http_${res.status}`, "The analyst couldn't start. Try again shortly.");
  }
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  const parse = createSseParser();
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    for (const m of parse(value)) onMessage(m);
  }
}

/** Streams SSE messages from `url` until the server closes the stream or `signal` aborts. */
export async function streamSse(
  url: string,
  { token, signal, onMessage }: { token: string; signal: AbortSignal; onMessage: (m: SseMessage) => void },
): Promise<void> {
  const res = await fetch(url, {
    headers: { accept: "text/event-stream", authorization: `Bearer ${token}` },
    credentials: "omit",
    signal,
  });
  if (!res.ok || !res.body) throw new Error(`Live events stream failed (${res.status})`);
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  const parse = createSseParser();
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    for (const m of parse(value)) onMessage(m);
  }
}
